import { Check, Copy, Info, Lock } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, isApiError } from '../../api';
import type { RegistryExchange } from '../../api/types';
import { Badge, type Tone } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { ReasonPrompt } from '../../components/DataError';
import { ErrorState, Loading } from '../../components/States';
import { formatDateTime } from '../../lib/format';
import { useLabels } from '../../lib/labels';
import { useApi } from '../../lib/useApi';
import { prettyWire } from '../../lib/wireText';
import { codeClass } from '../domains/refs';

const DAY_MS = 86_400_000;
const RESULT_TONE: Record<string, Tone> = { success: 'ok', rejected: 'warn', unknown: 'serious', error: 'crit' };

/**
 * The exchanges of one registry command as sent and received (EPP XML, REST/SOAP request and
 * response), read from the log store by clTRID through the Observability API, which opens them
 * server-side and audits every view with the reason the staff member states (ARCHITECTURE §10
 * "Registry exchange log", ADR-015).
 */
export function RegistryExchanges({ cltrid, startedAt }: { cltrid: string; startedAt: string }) {
  const { t } = useTranslation('domains');
  const result = useApi(
    async (signal) => {
      const view = await api.getRegistryExchanges(cltrid, { signal });
      // Only an empty answer needs the retention, to tell "cleaned up" from "never logged".
      const retention = view.exchanges.length ? null : await api.getLogRetention({ signal }).catch(() => null);
      const age = Math.floor((Date.now() - new Date(startedAt).getTime()) / DAY_MS);
      return { view, retention, age };
    },
    [cltrid, startedAt],
  );

  if (result.status === 'loading') return <Loading label={t('registryTx.exchange.loading')} />;
  if (result.status === 'error') {
    const e = result.error;
    if (isApiError(e) && e.code === 'BACKEND_NOT_CONFIGURED') return <Callout>{t('registryTx.exchange.storeOff')}</Callout>;
    // The payload holds contact data: the server asks for a stated reason first (ADR-015).
    if (isApiError(e) && e.code === 'REASON_REQUIRED') return <ReasonPrompt intro={t('registryTx.exchange.reasonIntro')} onSubmitted={result.reload} />;
    if (isApiError(e) && e.status === 403) {
      return (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{e.code === 'MFA_REQUIRED' ? t('registryTx.exchange.mfa') : t('registryTx.withheld')}</span>
        </p>
      );
    }
    return <ErrorState error={e} onRetry={result.reload} title={t('registryTx.exchange.loadFailed')} />;
  }

  const { view, retention, age } = result.data;
  if (!view.exchanges.length) {
    if (retention && age > retention.registry_exchange_days) {
      return (
        <Callout tone="warn" title={t('registryTx.exchange.expiredTitle')}>
          {t('registryTx.exchange.expired', { days: retention.registry_exchange_days, age })}{' '}
          <Link to="/settings?tab=security&sub=logs" className="text-primary hover:underline">
            {t('registryTx.exchange.retentionSetting')}
          </Link>
        </Callout>
      );
    }
    return <Callout title={t('registryTx.exchange.noneTitle')}>{t('registryTx.exchange.none')}</Callout>;
  }
  return (
    <div className="grid grid-cols-1 gap-4">
      {view.exchanges.map((x) => (
        <Exchange key={x.exchange_id} x={x} />
      ))}
    </div>
  );
}

function Exchange({ x }: { x: RegistryExchange }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const known = (k: string): k is 'success' | 'rejected' | 'unknown' | 'error' => Object.hasOwn(RESULT_TONE, k);
  const facts: ReactNode[] = [
    x.svtrid ? (
      <span key="sv">
        svTRID <code className={codeClass}>{x.svtrid}</code>
      </span>
    ) : null,
    x.session_id ? (
      <span key="se">
        {t('registryTx.exchange.session')} <code className={codeClass}>{x.session_id}</code>
      </span>
    ) : null,
    x.latency_ms ? <span key="l">{x.latency_ms} ms</span> : null,
    <span key="at">
      {t('registryTx.exchange.at')} {formatDateTime(x.logged_at)}
    </span>,
  ].filter(Boolean);

  return (
    <article className="space-y-3 rounded-lg border p-3">
      <header className="flex flex-wrap items-center gap-2 text-sm">
        <Badge tone="neutral" icon={false}>
          {x.protocol}
        </Badge>
        <span className="font-medium" title={x.operation}>
          {L.eppCommand(x.operation)}
        </span>
        {x.http_method ? (
          <code className={codeClass}>
            {x.http_method} {x.http_path}
            {x.http_status ? ` → HTTP ${x.http_status}` : ''}
          </code>
        ) : null}
        <Badge tone={RESULT_TONE[x.result] ?? 'neutral'}>{known(x.result) ? t(`registryTx.exchange.result.${x.result}`) : x.result}</Badge>
        {x.result_code ? <code className={codeClass}>{x.result_code}</code> : null}
        {x.result_message ? <span className="text-muted-foreground">{x.result_message}</span> : null}
      </header>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">{facts}</div>
      {x.payload_status === 'ok' ? (
        <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
          <WireText label={t('registryTx.exchange.request')} text={x.request} />
          <WireText label={t('registryTx.exchange.response')} text={x.response} />
        </div>
      ) : (
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{t(`registryTx.exchange.payload.${x.payload_status}`, { defaultValue: t('registryTx.exchange.payload.absent') })}</span>
        </p>
      )}
    </article>
  );
}

function WireText({ label, text }: { label: string; text: string | null }) {
  const { t } = useTranslation('domains');
  const [copied, setCopied] = useState(false);
  if (!text) {
    return (
      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
        <div className="mb-1 font-medium text-foreground">{label}</div>
        {t('registryTx.exchange.noResponse')}
      </div>
    );
  }
  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <div className="min-w-0 rounded-md border bg-muted/30">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-1.5 text-xs font-medium">
        <span>{label}</span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {copied ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
          {copied ? t('registryTx.exchange.copied') : t('registryTx.exchange.copy')}
        </button>
      </div>
      <pre className="max-h-[32rem] overflow-auto p-3 font-mono text-xs leading-relaxed whitespace-pre">{prettyWire(text)}</pre>
    </div>
  );
}
